import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit } from 'lucide-react';
import api from '@/lib/api';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    staff_name: '',
    google_calendar_id: '',
    services_offered: [],
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const response = await api.get('/staff');
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await api.put(`/staff/${editingStaff.id}`, formData);
        toast.success('Staff updated successfully');
      } else {
        await api.post('/staff', formData);
        toast.success('Staff added successfully');
      }
      setDialogOpen(false);
      setEditingStaff(null);
      setFormData({ staff_name: '', google_calendar_id: '', services_offered: [] });
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      staff_name: staffMember.staff_name,
      google_calendar_id: staffMember.google_calendar_id || '',
      services_offered: staffMember.services_offered || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success('Staff deleted successfully');
      loadStaff();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  return (
    <Layout active="staff">
      <div data-testid="staff-page" className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Staff Management
            </h1>
            <p className="text-gray-600 mt-2">Manage your team members and their calendars</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-button" onClick={() => {
                setEditingStaff(null);
                setFormData({ staff_name: '', google_calendar_id: '', services_offered: [] });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle>
                <DialogDescription>
                  {editingStaff ? 'Update staff member details' : 'Add a new team member'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="staff_name">Staff Name</Label>
                  <Input
                    id="staff_name"
                    data-testid="staff-name-input"
                    placeholder="John Doe"
                    value={formData.staff_name}
                    onChange={(e) => setFormData({ ...formData, staff_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google_calendar_id">Google Calendar ID (optional)</Label>
                  <Input
                    id="google_calendar_id"
                    data-testid="calendar-id-input"
                    placeholder="email@example.com"
                    value={formData.google_calendar_id}
                    onChange={(e) => setFormData({ ...formData, google_calendar_id: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Usually their Google email address</p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="staff-form-submit">
                    {editingStaff ? 'Update' : 'Add'} Staff
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : staff.length === 0 ? (
          <Card className="bg-white border-border shadow-sm">
            <CardContent className="text-center py-12">
              <p className="text-gray-500 mb-4">No staff members added yet</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-staff-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Staff Member
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {staff.map((member) => (
              <Card key={member.id} className="bg-white border-border shadow-sm" data-testid={`staff-card-${member.id}`}>
                <CardHeader>
                  <CardTitle className="flex justify-between items-start">
                    <span>{member.staff_name}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(member)}
                        data-testid={`edit-staff-${member.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(member.id)}
                        data-testid={`delete-staff-${member.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Calendar:</span>{' '}
                      {member.google_calendar_id || 'Not connected'}
                    </p>
                    {member.services_offered && member.services_offered.length > 0 && (
                      <p className="text-gray-600">
                        <span className="font-medium">Services:</span>{' '}
                        {member.services_offered.join(', ')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
